import { useEffect, useState } from 'react';
import './App.css';
import appMockup from './assets/hapynobg.png';

// ── Replace with your real store URLs ───────────────────────
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.thapy.app';
const APP_STORE_URL = 'https://apps.apple.com/in/app/thapy/id6670337035';
// ────────────────────────────────────────────────────────────

function Navbar() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <nav className={`nav${scrolled ? ' scrolled' : ''}`}>
      <div className="nav-inner">
        <a href="#home" className="nav-logo">
          <span className="nav-logo-text">thapy<span className="nav-logo-dot">.</span></span>
        </a>
        <div className="nav-links">
          <a href="#why" className="nav-link">Why Thapy</a>
          <a href="#features" className="nav-link">Features</a>
          <a href="#how-it-works" className="nav-link">How It Works</a>
        </div>
      </div>
    </nav>
  );
}

function Hero() {
  return (
    <section id="home" className="hero">
      <div className="hero-blob-1" />
      <div className="hero-blob-2" />
      <div className="hero-inner">
        <div className="hero-content">
          <div className="hero-eyebrow">
            <i className="bi bi-people-fill" />
            Peer support
          </div>
          <h1 className="hero-title">
            You Talk.<br />
            <span className="accent">We Listen.</span>
          </h1>
          <p className="hero-sub">
            Connect with real peers who understand what you're going through.
            Anonymous, judgment-free conversations whenever you need them —
            because sometimes you just need someone to talk to.
          </p>

          <p className="store-row-label">Download the app — it's free</p>
          <div className="store-row">
            <a href={PLAY_STORE_URL} className="store-btn store-btn-play" target="_blank" rel="noreferrer">
              <i className="bi bi-google-play store-btn-icon" />
              <span className="store-btn-text">
                <span className="store-btn-label">Get it on</span>
                <span className="store-btn-name">Google Play</span>
              </span>
            </a>
            <a href={APP_STORE_URL} className="store-btn store-btn-apple" target="_blank" rel="noreferrer">
              <i className="bi bi-apple store-btn-icon" />
              <span className="store-btn-text">
                <span className="store-btn-label">Download on the</span>
                <span className="store-btn-name">App Store</span>
              </span>
            </a>
          </div>
        </div>

        <div className="hero-visual">
          <div className="hero-img-wrap">
            <div className="hero-img-shadow" />
            <img src={appMockup} alt="Thapy App" className="hero-img" />
          </div>
        </div>
      </div>
    </section>
  );
}

function Why() {
  const values = [
    {
      icon: 'bi-person-heart',
      title: 'Real People, Real Empathy',
      text: "Your peers aren't professionals — they're real people who've been through similar things and genuinely want to listen, without judgment.",
    },
    {
      icon: 'bi-incognito',
      title: 'Fully Anonymous',
      text: "Talk freely. Your identity stays private so you can open up about things you'd never say out loud anywhere else.",
    },
    {
      icon: 'bi-chat-dots-fill',
      title: 'No Pressure, Ever',
      text: "No sessions to book, no fees, no agenda. Just reach out whenever you feel like it — someone is always there.",
    },
  ];

  return (
    <section id="why" className="section section-alt">
      <div className="container">
        <div className="section-header">
          <span className="section-label">Why Thapy</span>
          <h2 className="section-title">Sometimes you just need<br />someone to talk to.</h2>
          <p className="section-sub">
            Not a diagnosis. Not professional advice. Just a real person who listens —
            because that's often all it takes to feel a little less alone.
          </p>
        </div>
        <div className="values-grid">
          {values.map((v) => (
            <div className="value-card" key={v.title}>
              <div className="value-icon"><i className={`bi ${v.icon}`} /></div>
              <h3 className="value-title">{v.title}</h3>
              <p className="value-text">{v.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Features() {
  const items = [
    {
      icon: 'bi-shuffle',
      title: 'Smart Peer Matching',
      text: "We match you with peers based on what you're going through, so conversations feel relevant from the very start.",
    },
    {
      icon: 'bi-shield-check',
      title: 'Safe & Moderated',
      text: 'Community guidelines and active moderation keep the space respectful, kind, and safe for everyone.',
    },
    {
      icon: 'bi-moon-stars-fill',
      title: 'Available Any Time',
      text: "Hard nights don't follow a schedule. Thapy is available around the clock, whenever you need to reach out.",
    },
    {
      icon: 'bi-journal-text',
      title: 'Self-Care Resources',
      text: 'Browse curated articles, guided exercises, and community posts to support your everyday mental well-being.',
    },
  ];

  return (
    <section id="features" className="section">
      <div className="container">
        <div className="section-header">
          <span className="section-label">Features</span>
          <h2 className="section-title">Built to make talking easier</h2>
          <p className="section-sub">
            Every part of Thapy is designed to lower the barrier to opening up.
          </p>
        </div>
        <div className="features-grid">
          {items.map((item) => (
            <div className="feature-card" key={item.title}>
              <div className="feature-icon">
                <i className={`bi ${item.icon}`} />
              </div>
              <div>
                <h3 className="feature-title">{item.title}</h3>
                <p className="feature-text">{item.text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    {
      n: '1',
      title: 'Download the App',
      text: 'Get Thapy free on Android or iOS. Create an anonymous profile — no real name needed.',
    },
    {
      n: '2',
      title: 'Find a Peer',
      text: "Tell us what's on your mind. We'll match you with a peer who understands what you're going through.",
    },
    {
      n: '3',
      title: 'Start Talking',
      text: "Chat at your own pace, in your own time. No commitments, no pressure — just a genuine conversation.",
    },
  ];

  return (
    <section id="how-it-works" className="section section-tinted">
      <div className="container">
        <div className="section-header centered">
          <span className="section-label">How It Works</span>
          <h2 className="section-title">Three steps to feeling heard</h2>
          <p className="section-sub">
            Getting support shouldn't be complicated. With Thapy, you're one tap away.
          </p>
        </div>
        <div className="steps-grid">
          <div className="steps-connector" />
          {steps.map((step) => (
            <div className="step" key={step.n}>
              <div className="step-num">{step.n}</div>
              <h3 className="step-title">{step.title}</h3>
              <p className="step-text">{step.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Download() {
  return (
    <section className="download">
      <div className="container">
        <h2 className="download-title">Ready to talk?</h2>
        <p className="download-sub">
          Download Thapy for free and connect with someone who gets it.
        </p>
        <div className="download-actions">
          <a href={PLAY_STORE_URL} className="store-btn store-btn-white" target="_blank" rel="noreferrer">
            <i className="bi bi-google-play store-btn-icon" style={{ color: '#4F46E5' }} />
            <span className="store-btn-text">
              <span className="store-btn-label">Get it on</span>
              <span className="store-btn-name" style={{ color: '#4F46E5' }}>Google Play</span>
            </span>
          </a>
          <a href={APP_STORE_URL} className="store-btn store-btn-outline" target="_blank" rel="noreferrer">
            <i className="bi bi-apple store-btn-icon" />
            <span className="store-btn-text">
              <span className="store-btn-label">Download on the</span>
              <span className="store-btn-name">App Store</span>
            </span>
          </a>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="footer">
      <div className="footer-inner">
        <div className="footer-top">
          <div className="footer-brand">
            <span className="nav-logo-text" style={{ color: '#fff' }}>thapy<span className="nav-logo-dot">.</span></span>
            <p>Peer-powered support. Real conversations with real people, any time.</p>
          </div>
          <div className="footer-links-group">
            <h4>Product</h4>
            <ul>
              <li><a href="#why">Why Thapy</a></li>
              <li><a href="#features">Features</a></li>
              <li><a href="#how-it-works">How It Works</a></li>
            </ul>
          </div>
          <div className="footer-links-group">
            <h4>Download</h4>
            <ul>
              <li><a href={PLAY_STORE_URL} target="_blank" rel="noreferrer">Google Play</a></li>
              <li><a href={APP_STORE_URL} target="_blank" rel="noreferrer">App Store</a></li>
            </ul>
          </div>
          <div className="footer-links-group">
            <h4>Company</h4>
            <ul>
              <li><a href="#why">About Us</a></li>
              <li><a href="mailto:hello@thapy.in">Contact</a></li>
            </ul>
          </div>
        </div>
        <hr className="footer-divider" />
        <div className="footer-bottom">
          <p className="footer-copy">© {new Date().getFullYear()} Thapy. All rights reserved.</p>
          <div className="footer-socials">
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a href="#" className="social-icon" aria-label="Instagram"><i className="bi bi-instagram" /></a>
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a href="#" className="social-icon" aria-label="Twitter / X"><i className="bi bi-twitter-x" /></a>
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a href="#" className="social-icon" aria-label="LinkedIn"><i className="bi bi-linkedin" /></a>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function App() {
  return (
    <>
      <Navbar />
      <Hero />
      <Why />
      <Features />
      <HowItWorks />
      <Download />
      <Footer />
    </>
  );
}
